import * as React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Ic } from './Icons';

// Scoped responsive swap for the two triggers below, mirroring the same 640px
// breakpoint AppShell's own top-bar elements already use for the search box /
// search-toggle pair (see `.crumb` / `.msearch-btn` in styles/screens.css) —
// kept local to this component (rather than editing the shared stylesheet)
// since this feature is meant to be a self-contained addition.
const RESPONSIVE_STYLE = `
  .org-switch-full { display: inline-flex; }
  .org-switch-compact { display: none; }
  @media (max-width: 640px) {
    .org-switch-full { display: none; }
    .org-switch-compact { display: inline-grid; }
    /* #377: on mobile the item-detail (.detail-overlay, z 46) and sidebar (z 48)
       are full-screen fixed overlays; an absolutely-positioned dropdown inside the
       in-flow topbar renders BEHIND them (only its top pixels peek out under the
       nav bar). Break the menu out to fixed + a z-index above those overlays,
       exactly like the notification popover fix (#359, styles/screens.css). */
    .org-switch-menu {
      position: fixed !important;
      top: 47px !important;
      left: auto !important;
      right: 6px !important;
      z-index: 60 !important;
    }
    .org-switch-scrim { z-index: 59 !important; }
  }
`;

/**
 * Current-organisation indicator + switcher (GH #250).
 *
 * Renders nothing when the signed-in user belongs to 0 or 1 org — that user
 * has nothing to switch between and already sees their org name in AppShell's
 * breadcrumb. It only appears for users who belong to *more than one* org,
 * which in practice is mostly super-admins and anyone invited into multiple
 * tenants — exactly the case GH #250 reports as having "no clear indication".
 *
 * Desktop shows the full org name in a pill button; mobile compacts to an
 * icon-only button (same full<->compact swap pattern AppShell already uses
 * for its search box vs. search-toggle icon). Both open the same dropdown
 * listing every org the caller is a member of.
 *
 * Switching calls `chooseOrg` (AuthContext), which persists the choice to
 * localStorage and mirrors it onto the shared `X-Org-Id` request header
 * (services/authToken.js `attachAuth` interceptor) that scopes every core/
 * chat API call. Every hook keyed on `activeOrg` (useTreeState's project
 * fetch, useProjectMap, ...) already depends on that context value and
 * re-fetches on change, so no page reload is needed for the tree/rest of the
 * app to reflect the new org.
 *
 * Only orgs the user is actually a member of are offered — the backend
 * (`tenancy._resolve_active_org`) 403s an `X-Org-Id` that isn't one of the
 * caller's own memberships, so a platform-wide "every org" list (which would
 * need a separate super-admin-only endpoint) is intentionally out of scope
 * here; the existing super-admin "Admin" console already covers that.
 */
export default function OrgSwitcher() {
  const { orgs, activeOrg, chooseOrg } = useAuth();
  const [open, setOpen] = React.useState(false);

  // #793 H / #149: Escape closes the dropdown. It used to be dismissible by POINTER
  // only (the scrim behind it, or re-clicking the trigger) — the reason that scrim was
  // a `<div onClick>` accessibility error. Marking the scrim decorative (`aria-hidden`,
  // below) is only honest once the keyboard has its own way out.
  // NOTE: declared ABOVE the `orgs.length < 2` early return — a hook after it would be
  // conditional (rules-of-hooks).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!orgs || orgs.length < 2) return null;

  const current = orgs.find((o) => o.id === activeOrg);
  const label = current ? current.name : 'Select organisation';

  const choose = (id) => {
    setOpen(false);
    if (id !== activeOrg) chooseOrg(id);
  };

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <style>{RESPONSIVE_STYLE}</style>

      <button
        type="button"
        className="org-switch-full"
        data-testid="org-switcher-full"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Switch organisation (current: ${label})`}
        onClick={() => setOpen((o) => !o)}
        style={{
          alignItems: 'center', gap: 6, maxWidth: 200,
          border: '1px solid var(--border)', background: 'var(--base)',
          color: 'var(--text)', borderRadius: 'var(--r-sm)',
          padding: '4px 8px 4px 9px', cursor: 'pointer', font: 'inherit',
        }}
      >
        <Ic name="users" sm />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 12.5 }}>
          {label}
        </span>
        <Ic name="chev" sm style={{ transform: 'rotate(90deg)', flexShrink: 0 }} />
      </button>

      <button
        type="button"
        className="icon-btn org-switch-compact"
        data-testid="org-switcher-compact"
        aria-label={`Switch organisation (current: ${label})`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((o) => !o)}
        style={{ borderColor: 'transparent', background: 'transparent' }}
      >
        <Ic name="users" />
      </button>

      {open && (
        <>
          <div className="org-switch-scrim" style={{ position: 'fixed', inset: 0, zIndex: 40 }} aria-hidden="true" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="cardp org-switch-menu"
            data-testid="org-switcher-menu"
            style={{ position: 'absolute', top: 34, left: 0, zIndex: 41, minWidth: 200, padding: 6 }}
          >
            <div className="t-over" style={{ padding: '4px 8px' }}>Organisations</div>
            {orgs.map((o) => (
              <button
                key={o.id}
                type="button"
                role="menuitem"
                className="snav"
                style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', font: 'inherit' }}
                onClick={() => choose(o.id)}
              >
                {o.id === activeOrg && <Ic name="check" sm />}
                <span style={{ marginLeft: o.id === activeOrg ? 0 : 22 }}>{o.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
